"use client";

import Image from "next/image";
import { Check } from "lucide-react";
import { useEffect, useRef, type CSSProperties } from "react";

import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import type { ShopifySubpageCopy } from "./shopify-page-copy";

type CcShopifyGalleryProps = {
  photos: ShopifySubpageCopy["hero"]["gallery"];
  chips: ShopifySubpageCopy["hero"]["chips"];
};

// Each card sits at its own height and drifts at its own speed (Charle's hero grid).
const OFFSETS = [24, 72, 0, 48, 12, 80, 32, 60];
const DEPTHS = [0.5, 1.1, 0.7, 1.3, 0.6, 1.2, 0.8, 1];

/**
 * Row of real photos under the hero copy. Cards are staggered and move at
 * different speeds as the page scrolls; two live-looking UI chips sit on
 * top. Photos load lazily; the scroll handler only writes one CSS variable.
 */
export function CcShopifyGallery({ photos, chips }: CcShopifyGalleryProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const reduced = useCcPrefersReducedMotion();

  useEffect(() => {
    const root = rootRef.current;
    if (!root || reduced) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = root.getBoundingClientRect();
      const progress = Math.min(1, Math.max(-1, (window.innerHeight - rect.top) / window.innerHeight - 0.5));
      root.style.setProperty("--cc-shop-scroll", progress.toFixed(3));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [reduced]);

  return (
    <div ref={rootRef} className="cc-shop-gallery" aria-hidden="true">
      <div className="cc-shop-gallery__track">
        {photos.map((photo, index) => (
          <figure
            key={photo.src}
            className="cc-shop-gallery__card"
            style={
              {
                "--cc-shop-offset": `${OFFSETS[index % OFFSETS.length]}px`,
                "--cc-shop-depth": DEPTHS[index % DEPTHS.length],
                "--cc-shop-i": index,
              } as CSSProperties
            }
          >
            <Image
              src={photo.src}
              alt=""
              width={photo.width}
              height={photo.height}
              sizes="(min-width: 1200px) 190px, (min-width: 640px) 24vw, 40vw"
              quality={75}
              // The H1 is the LCP; these load as they enter the viewport.
              loading="lazy"
            />
            {index === 1 ? (
              <span className="cc-shop-chip cc-shop-chip--order">
                <span className="cc-shop-chip__logo">
                  <Image src={chips.order.logo} alt="" width={16} height={16} unoptimized />
                </span>
                <span>
                  <small>{chips.order.title}</small>
                  <strong>{chips.order.value}</strong>
                </span>
              </span>
            ) : null}
            {index === 4 ? (
              <span className="cc-shop-chip cc-shop-chip--sync">
                <span className="cc-shop-chip__logo">
                  <Image src={chips.sync.logo} alt="" width={16} height={16} unoptimized />
                </span>
                <strong>{chips.sync.title}</strong>
                <Check size={13} strokeWidth={2.4} className="cc-shop-chip__check" />
              </span>
            ) : null}
            {index === 6 ? (
              <span className="cc-shop-chip cc-shop-chip--live">
                <i />
                <strong>{chips.live.title}</strong>
              </span>
            ) : null}
          </figure>
        ))}
      </div>
    </div>
  );
}
